<template>
  <div class="d-flex align-center flex-shrink-0 defect-module-row-trailing">
    <div class="solvit-de-trailing-defect-col d-flex align-center justify-start min-width-0">
      <span
        v-if="!hasDefects"
        class="defect-module-row-empty text-caption text--secondary text-truncate"
      >
        {{ emptyLabel }}
      </span>
      <div
        v-else
        class="d-flex align-center justify-start defect-module-type-badges"
        aria-hidden="true"
      >
        <span
          v-for="item in visibleBadgeItems"
          :key="badgeKey(item.defect)"
          class="defect-module-type-icon-badge"
          :title="badgeTitle(item)"
        >
          <img
            class="defect-module-type-icon-badge__img"
            :src="iconFor(item.defect)"
            :alt="labelFor(item.defect)"
            width="24"
            height="24"
            @error="handleIconError"
          />
          <span
            v-if="item.count > 1"
            class="defect-module-type-icon-badge__count"
          >×{{ item.count }}</span>
        </span>
        <span
          v-if="overflowCount > 0"
          class="defect-module-type-icon-badge defect-module-type-icon-badge--more"
          :title="overflowTitle"
        >+{{ overflowCount }}</span>
      </div>
    </div>
    <div
      class="solvit-de-trailing-edit-col d-flex align-center justify-center flex-shrink-0"
      @click.stop
    >
      <v-btn
        v-if="showEdit && primaryDefect"
        icon
        class="defect-module-edit-btn"
        :aria-label="editLabel"
        @click.stop="$emit('edit', primaryDefect)"
      >
        <v-icon class="defect-module-edit-btn__icon">mdi-pencil-outline</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
  const MAX_TYPE_BADGES = 3;

  export default {
    name: 'DefectModuleRowTrailing',
    props: {
      defects: { type: Array, default: () => [] },
      showEdit: { type: Boolean, default: true },
      emptyLabel: { type: String, default: '' },
      editLabel: { type: String, default: '' },
      iconSrcFn: { type: Function, required: true },
      labelFn: { type: Function, required: true },
      onIconError: { type: Function, default: null }
    },
    computed: {
      hasDefects() {
        return (this.defects || []).some(Boolean);
      },
      badgeItems() {
        const map = new Map();
        (this.defects || []).forEach(defect => {
          if (!defect) return;
          const key = String(defect.defectTypeCode ?? defect.defectType ?? defect.seq ?? '');
          if (!map.has(key)) {
            map.set(key, { defect, count: 1 });
          } else {
            map.get(key).count += 1;
          }
        });
        return [...map.values()];
      },
      visibleBadgeItems() {
        return this.badgeItems.slice(0, MAX_TYPE_BADGES);
      },
      overflowCount() {
        return Math.max(0, this.badgeItems.length - MAX_TYPE_BADGES);
      },
      overflowTitle() {
        if (!this.overflowCount) return '';
        return this.badgeItems
          .slice(MAX_TYPE_BADGES)
          .map(item => this.badgeTitle(item))
          .join(', ');
      },
      primaryDefect() {
        const defects = this.defects?.filter(Boolean) ?? [];
        return defects[0] ?? null;
      }
    },
    methods: {
      badgeKey(defect) {
        return String(defect?.seq ?? defect?.defectTypeCode ?? defect?.defectType ?? '');
      },
      iconFor(defect) {
        return this.iconSrcFn(defect);
      },
      labelFor(defect) {
        return this.labelFn(defect);
      },
      badgeTitle(item) {
        const label = this.labelFor(item.defect);
        return item.count > 1 ? `${label} ×${item.count}` : label;
      },
      handleIconError(event) {
        if (typeof this.onIconError === 'function') {
          this.onIconError(event);
        }
      }
    }
  };
</script>

<style scoped>
  .defect-module-row-trailing {
    padding-right: 4px;
    max-width: 100%;
    min-width: 0;
    gap: 10px;
  }

  .solvit-de-trailing-defect-col {
    flex: 1 1 0;
    min-width: 104px;
    max-width: none;
    overflow: visible;
    padding-right: 6px;
    justify-content: flex-start;
  }

  .solvit-de-trailing-edit-col {
    flex: 0 0 32px;
    width: 32px;
  }

  .defect-module-row-empty {
    display: block;
    max-width: 100%;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .defect-module-type-badges {
    gap: 6px;
    flex-wrap: nowrap;
    overflow: visible;
    width: 100%;
    max-width: 100%;
    flex-shrink: 1;
    min-width: 0;
    justify-content: flex-start;
  }

  .defect-module-type-icon-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: 32px;
    height: 28px;
    padding: 0;
    margin-right: 0;
    flex-shrink: 0;
    pointer-events: none;
  }

  .defect-module-type-icon-badge__img {
    display: block;
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  .defect-module-type-icon-badge__count {
    position: absolute;
    right: -2px;
    bottom: -1px;
    min-width: 16px;
    padding: 1px 3px;
    font-size: 11px;
    line-height: 1.15;
    color: var(--v-error-base, #f44336);
    font-weight: 700;
    text-align: center;
    background: var(--v-surface-base, #fff);
    border-radius: 8px;
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--v-error-base, #f44336) 35%, transparent);
    pointer-events: none;
  }

  .defect-module-type-icon-badge--more {
    font-size: 11px;
    color: var(--v-text-secondary, #666);
    width: auto;
    min-width: 22px;
    height: 28px;
  }

  .defect-module-edit-btn {
    flex-shrink: 0;
    width: 32px !important;
    height: 32px !important;
  }

  .defect-module-edit-btn__icon {
    font-size: 22px !important;
  }
</style>
